import {
  ColorCategories,
  Coloring,
  ColoringType,
  DataLayer,
  DataLayerType,
  FlatLegendType,
} from '../../api/api.ts';
import classNames from 'classnames';
import { Fragment, ReactNode, useContext, useMemo } from 'react';
import chroma from 'chroma-js';
import Controls from '../Controls.tsx';
import { useMapContext } from '../Map.tsx';
import cn from '../../utils/cn.ts';
import { LanguageContext } from '@/common/Language.tsx';

const ColorDot = ({ color, label }: { color: string; label: string }) => {
  return (
    <div>
      <span
        className={classNames(
          'inline-block',
          'w-[16px]',
          'h-[16px]',
          'rounded-[4px]',
          'shadow-xs',
          'mr-[6px]',
          'align-middle',
          '-translate-y-[2px]'
        )}
        style={{ backgroundColor: color }}
      ></span>
      <span>{label}</span>
    </div>
  );
};
const ColorScale = ({
  scale,
  minLabel,
  maxLabel,
}: {
  scale: string[];
  minLabel: string;
  maxLabel: string;
}) => {
  const gradient = useMemo(() => {
    const colors = chroma.scale(scale).mode('lch').colors(10);
    return `linear-gradient(to right, ${colors.join(', ')})`;
  }, [scale]);

  return (
    <div>
      <div
        className={classNames(
          'relative',
          'h-[16px]',
          'rounded-[4px]',
          'shadow-xs',
          'mb-[28px]'
        )}
        style={{ background: gradient }}
      >
        <span className={classNames('absolute', '-bottom-[28px]', 'left-0')}>
          {minLabel}
        </span>
        <span className={classNames('absolute', '-bottom-[28px]', 'right-0')}>
          {maxLabel}
        </span>
      </div>
    </div>
  );
};

const TranparencyScale = ({
  color,
  minLabel,
  maxLabel,
}: {
  color: string;
  minLabel: string;
  maxLabel: string;
}) => {
  const gradient = useMemo(() => {
    return `linear-gradient(to right, ${chroma(color).alpha(0)}, ${chroma(
      color
    ).alpha(1)})`;
  }, [color]);

  return (
    <div>
      <div
        className={classNames(
          'relative',
          'rounded-[4px]',
          'h-[16px]',
          'shadow-xs',
          'mb-[28px]'
        )}
        style={{ background: gradient }}
      >
        <span className={classNames('absolute', '-bottom-[28px]', 'left-0')}>
          {minLabel}
        </span>
        <span className={classNames('absolute', '-bottom-[28px]', 'right-0')}>
          {maxLabel}
        </span>
      </div>
    </div>
  );
};

const ColoringLegend = ({ coloring }: { coloring: Coloring }): ReactNode => {
  switch (coloring.type) {
    case ColoringType.Flat: {
      return coloring.legend.type === FlatLegendType.Flat ? (
        <div>
          <ColorDot color={coloring.color} label={coloring.legend.label} />
        </div>
      ) : (
        <div>
          <TranparencyScale
            color={coloring.color}
            minLabel={coloring.legend.minLabel}
            maxLabel={coloring.legend.maxLabel}
          />
        </div>
      );
    }
    case ColoringType.Scale: {
      return (
        <div>
          <ColorScale
            scale={coloring.colors}
            minLabel={coloring.legend.minLabel}
            maxLabel={coloring.legend.maxLabel}
          />
        </div>
      );
    }
    case ColoringType.Bands: {
      return (
        <div>
          {coloring.bands.map((band, index, _bandArray) => {
            return (
              <ColorDot key={index} color={band.color} label={band.legend} />
            );
          })}
        </div>
      );
    }
    case ColoringType.Categories: {
      return (
        <div>
          {Object.keys(coloring.colorMap).map((key) => {
            const { color, legend } = (coloring as ColorCategories).colorMap[
              key
            ];
            return <ColorDot color={color} label={legend} />;
          })}
        </div>
      );
    }
    default:
      return <></>;
  }
};

export default function Legend({
  className,
  dataLayers,
}: {
  className?: string;
  dataLayers: DataLayer[];
}): ReactNode {
  const { getParameterValues, updateParameterValues } = useMapContext();
  const { t } = useContext(LanguageContext);

  return dataLayers.length ? (
    <div className={classNames(className)}>
      {dataLayers.map((layer, index, array) => (
        <div key={layer.id}>
          {index !== 0 && (
            <div
              className={cn(
                'mt-[12px]',
                '-mx-[12px]',
                'border-y-1',
                'border-b-white',
                'border-t-gray-200'
              )}
            ></div>
          )}
          <div className={cn('font-semibold', index ? 'mt-[12px]' : null)}>
            {t(layer.shortName, layer.shortNameDe)}
          </div>
          <div className={cn('mt-[12px]')}></div>
          {layer.type === DataLayerType.Data &&
            layer.dataSources.map((source, index) => (
              <Fragment key={index}>
                {index > 0 && <div className={cn('mt-[6px]')}></div>}
                <ColoringLegend coloring={source.coloring} />
              </Fragment>
            ))}
          <div className={cn('mt-[12px]')}></div>
          <Controls
            layer={layer}
            parameterValues={getParameterValues(layer.id)}
            updateLayerParams={(settings) => {
              updateParameterValues(layer.id, settings);
            }}
          />
        </div>
      ))}
    </div>
  ) : null;
}
